import { useCallback, useEffect, useState } from 'react';
import axios from 'src/utils/axios';

export interface Language {
  code: string;
  label: string;
}

export const LANGUAGES: Language[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'ht', label: 'Kreyòl Ayisyen' },
  { code: 'ru', label: 'Русский' },
  { code: 'yue', label: '廣東話' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'ar', label: 'العربية' },
  { code: 'fil', label: 'Filipino' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'ko', label: '한국어' },
  { code: 'yi', label: 'ייִדיש' },
  { code: 'am', label: 'አማርኛ' },
  { code: 'pt', label: 'Português' },
  { code: 'fa', label: 'فارسی' },
  { code: 'tl', label: 'Tagalog' },
];

/** Read the current language from the googtrans cookie.
 *  Returns the language code (e.g. 'es') or 'en' for English (no cookie). */
export const getGoogleTranslateCookie = (): string => {
  const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
  return match ? match[1] : 'en';
};

const getParentDomain = (hostname: string): string | null => {
  const parts = hostname.split('.');
  return parts.length > 2 ? parts.slice(1).join('.') : null;
};

const clearAllGoogleTranslateCookies = (): void => {
  const hostname = window.location.hostname;
  const parentDomain = getParentDomain(hostname);
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
  document.cookie = `googtrans=; ${expired}; path=/;`;
  document.cookie = `googtrans=; ${expired}; path=/; domain=${hostname};`;
  document.cookie = `googtrans=; ${expired}; path=/; domain=.${hostname};`;
  if (parentDomain) {
    document.cookie = `googtrans=; ${expired}; path=/; domain=${parentDomain};`;
    document.cookie = `googtrans=; ${expired}; path=/; domain=.${parentDomain};`;
  }
};

/** Apply a language code to the Google Translate cookie and reload.
 *  Called by the WS event handler after the server confirms the preference. */
export const applyGoogleTranslateLanguage = (langCode: string): void => {
  clearAllGoogleTranslateCookies();
  if (langCode && langCode !== 'en') {
    document.cookie = `googtrans=/en/${langCode}; path=/;`;
  }
  window.location.reload();
};

const ensureGoogleTranslateLoaded = (): void => {
  if (window.googleTranslateScriptAdded) {
    return;
  }
  window.googleTranslateElementInit = () => {
    const googleObj = window.google;
    if (googleObj?.translate) {
      new googleObj.translate.TranslateElement(
        {
          pageLanguage: 'en',
          includedLanguages:
            'es,fr,ht,ru,yue,zh-CN,ar,fil,vi,ko,yi,am,pt,fa,tl',
          autoDisplay: false,
        },
        'google_translate_element_hidden'
      );
    }
  };
  const script = document.createElement('script');
  script.src =
    '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  document.body.appendChild(script);
  window.googleTranslateScriptAdded = true;
};

interface UseGoogleTranslateReturn {
  currentLang: string;
  currentLanguageLabel: string;
  selectLanguage: (langCode: string) => void;
  saving: boolean;
}

const useGoogleTranslate = (): UseGoogleTranslateReturn => {
  const [currentLang, setCurrentLang] = useState<string>('en');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCurrentLang(getGoogleTranslateCookie());
    ensureGoogleTranslateLoaded();
  }, []);

  /** PATCH the user profile with the new language preference.
   *  The WS broadcast on user_profile_updated will trigger
   *  applyGoogleTranslateLanguage → cookie set → page reload. */
  const selectLanguage = useCallback((langCode: string): void => {
    setSaving(true);
    // Optimistically update UI so the checkmark moves immediately
    setCurrentLang(langCode);
    void (async (): Promise<void> => {
      try {
        await axios.patch('v2/user/contacts/', {
          language_preference: langCode,
        });
        // WS broadcast will fire applyGoogleTranslateLanguage via UserUpdateManager.
        // If WS is unavailable, fall back to applying directly after a short delay.
        setTimeout(() => {
          applyGoogleTranslateLanguage(langCode);
        }, 3000);
      } catch {
        // Revert optimistic update
        setCurrentLang(getGoogleTranslateCookie());
      } finally {
        setSaving(false);
      }
    })();
  }, []);

  const currentLanguageLabel =
    LANGUAGES.find((l) => l.code === currentLang)?.label ?? 'English';

  return { currentLang, currentLanguageLabel, selectLanguage, saving };
};

export default useGoogleTranslate;
